import { Database } from '@nozbe/watermelondb';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { createDemoAdapter } from './adapter';
import DemoProduct from './models/DemoProduct';
import DemoCartItem from './models/DemoCartItem';
import DemoOrderMaster from './models/DemoOrderMaster';
import DemoPaymentInformation from './models/DemoPaymentInformation';
import DemoSaleInformation from './models/DemoSaleInformation';
import {
  DEMO_CATALOG_VERSION,
  DEMO_CATALOG_VERSION_STORAGE_KEY,
  DEMO_PRODUCTS,
} from './demoData';

let demoDatabaseInstance: Database | null = null;

export function getDemoDatabase(): Database {
  if (!demoDatabaseInstance) {
    const adapter = createDemoAdapter();

    demoDatabaseInstance = new Database({
      adapter,
      modelClasses: [
        DemoProduct,
        DemoCartItem,
        DemoOrderMaster,
        DemoPaymentInformation,
        DemoSaleInformation,
      ],
    });
  }
  return demoDatabaseInstance;
}

// Flag to track if seeding has been done this session
let isSeeded = false;

async function getStoredCatalogVersion(): Promise<number | null> {
  try {
    const raw = await AsyncStorage.getItem(DEMO_CATALOG_VERSION_STORAGE_KEY);
    if (raw == null) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

async function setStoredCatalogVersion(version: number): Promise<void> {
  await AsyncStorage.setItem(
    DEMO_CATALOG_VERSION_STORAGE_KEY,
    String(version),
  );
}

async function writeDemoProducts(productsCollection: any): Promise<void> {
  for (const productData of DEMO_PRODUCTS) {
    await productsCollection.create((product: DemoProduct) => {
      product.bpOwnerId = productData.bp_owner_id;
      product.bpProductLocationId = productData.bp_product_location_id;
      product.bpProductBarcode = productData.bp_product_barcode;
      product.bpProductName = productData.bp_product_name;
      product.bpProductCategory = productData.bp_product_category;
      product.bpProductSubCategory = productData.bp_product_sub_category;
      product.bpProductPriceType = productData.bp_product_price_type;
      product.bpProductUnitOfMass = productData.bp_product_unit_of_mass;
      product.bpProductMass = productData.bp_product_mass;
      // WM columns are non-optional; variant parents may seed null → 0
      product.bpProductBuyingPrice = productData.bp_product_buying_price ?? 0;
      product.bpProductSellingPrice = productData.bp_product_selling_price ?? 0;
      product.bpProductQuantity = productData.bp_product_quantity ?? 0;
      product.bpProductSize = productData.bp_product_size;
      product.bpMinimumStockLevel = productData.bp_minimum_stock_level;
      product.bpProductModel = productData.bp_product_model;
      product.bpProductImage = productData.bp_product_image;
      product.bpProductDeleted = productData.bp_product_deleted;
      product.bpProductIsFavourite = productData.bp_product_is_favourite;
      product.bpProductCreatedAt = productData.bp_product_created_at;
      product.bpProductUpdatedAt = productData.bp_product_updated_at;
      product.bpProductHasVariants = Boolean(
        productData.bp_product_has_variants,
      );
      product.bpProductVariantAttributes = JSON.stringify(
        productData.bp_product_variant_attributes ?? [],
      );
      product.bpProductVariants = JSON.stringify(
        productData.bp_product_variants ?? [],
      );
    });
  }
}

// Seed demo products if needed. Catalog version mismatch forces full clear + reseed.
export async function seedDemoProducts(): Promise<void> {
  try {
    const storedVersion = await getStoredCatalogVersion();
    // Allow mid-session catalog bumps to force reseed even if already seeded
    if (storedVersion !== DEMO_CATALOG_VERSION) {
      isSeeded = false;
    }

    if (isSeeded) {
      return;
    }

    const db = getDemoDatabase();
    const productsCollection = db.get<DemoProduct>('demo_products');
    const existingCount = await productsCollection.query().fetchCount();
    const needsReseed =
      storedVersion !== DEMO_CATALOG_VERSION || existingCount === 0;

    if (needsReseed) {
      if (existingCount > 0 || storedVersion !== DEMO_CATALOG_VERSION) {
        // Clear stale demo rows so the new catalog always wins
        await clearDemoData({ preserveSeedFlag: true });
      }

      await db.write(async () => {
        await writeDemoProducts(productsCollection);
      });
      await setStoredCatalogVersion(DEMO_CATALOG_VERSION);
      console.log(
        '[DemoDatabase] Seeded',
        DEMO_PRODUCTS.length,
        'demo products (catalog v' + DEMO_CATALOG_VERSION + ')',
      );
    }

    isSeeded = true;
  } catch (error) {
    console.error('[DemoDatabase] Seed error:', error);
    throw error;
  }
}

// Clear all demo data (useful for resetting demo flow)
export async function clearDemoData(options?: {
  preserveSeedFlag?: boolean;
}): Promise<void> {
  try {
    const db = getDemoDatabase();
    await db.write(async () => {
      const products = await db
        .get<DemoProduct>('demo_products')
        .query()
        .fetch();
      const cartItems = await db
        .get<DemoCartItem>('demo_cart_items')
        .query()
        .fetch();
      const orderMasters = await db
        .get<DemoOrderMaster>('demo_order_master_information')
        .query()
        .fetch();
      const payments = await db
        .get<DemoPaymentInformation>('demo_payment_information')
        .query()
        .fetch();
      const sales = await db
        .get<DemoSaleInformation>('demo_sales_information')
        .query()
        .fetch();

      for (const item of [
        ...products,
        ...cartItems,
        ...orderMasters,
        ...payments,
        ...sales,
      ]) {
        await item.destroyPermanently();
      }
    });
    if (!options?.preserveSeedFlag) {
      isSeeded = false;
      try {
        await AsyncStorage.removeItem(DEMO_CATALOG_VERSION_STORAGE_KEY);
      } catch {
        // ignore storage clear failures
      }
    }
    console.log('[DemoDatabase] Cleared all demo data');
  } catch (error) {
    console.error('[DemoDatabase] Clear error:', error);
    throw error;
  }
}

// Export models for direct use
export {
  DemoProduct,
  DemoCartItem,
  DemoOrderMaster,
  DemoPaymentInformation,
  DemoSaleInformation,
};
export {
  DEMO_PRODUCTS,
  DEMO_CATALOG_VERSION,
  DEMO_CATALOG_VERSION_STORAGE_KEY,
} from './demoData';
